import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Card,
  Typography,
  TextField,
  Button,
  Stack,
  useTheme,
  alpha,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem,
  Grid,
  FormControlLabel,
  Switch,
  Card as MuiCard,
} from '@mui/material';
import { CalendarToday, ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getTutorByUser, updateTutor, clearTutor } from '../../state/slices/tutor';
import DateAvailabilityCalendar from '../../components/calendar/DateAvailabilityCalendar';

function TutorCalendarForm() {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tutorState = useSelector((state) => state.tutor);
  const userState = useSelector((state) => state.user).user;

  const ACTIVE_STATUS = [t('tutorProfile.active'), t('tutorProfile.inActive')];

  const [formData, setFormData] = useState({
    currentStatus: '',
    tutoringType: [],
    dateAvailability: [],
  });
  const [errors, setErrors] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  useEffect(() => {
    if (userState && userState.roles.includes('TUTOR') && !tutorState.tutor) {
      dispatch(getTutorByUser(userState.id));
    }
  }, [userState, dispatch, tutorState.tutor]);

  useEffect(() => {
    if (tutorState.tutor) {
      // Clean dateAvailability to remove any extra fields (like 'default' from Mongoose)
      const cleanedDateAvailability = (tutorState.tutor.dateAvailability || []).map(item => ({
        date: item.date,
        timeSlots: Array.isArray(item.timeSlots) ? item.timeSlots : []
      }));
      
      setFormData({
        currentStatus: tutorState.tutor.currentStatus || '',
        tutoringType: tutorState.tutor.tutoringType || [],
        dateAvailability: cleanedDateAvailability,
      });
    }
  }, [tutorState.tutor]);

  useEffect(() => {
    // Handle "Tutor not found" gracefully - it's expected for new tutors
    if (tutorState.error && tutorState.error !== 'Tutor not found') {
      setOpenSnackbar(true);
      setSubmitStatus('error');
      setErrors((prev) => ({ ...prev, general: tutorState.error }));
      dispatch(clearTutor());
    } else if (tutorState.error === 'Tutor not found') {
      // Tutor profile doesn't exist yet - this is fine, just clear the error
      dispatch(clearTutor());
    } else if (tutorState.successMsg && tutorState.tutor) {
      setOpenSnackbar(true);
      setSubmitStatus('success');
      setTimeout(() => {
        dispatch(clearTutor());
        navigate('/tutor-profile');
      }, 2000);
    }
  }, [tutorState.error, tutorState.successMsg, tutorState.tutor, navigate, dispatch]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleTutoringTypeChange = (event) => {
    const { value } = event.target;
    const selectedTypes = [...formData.tutoringType];

    if (event.target.checked) {
      selectedTypes.push(value);
    } else {
      const index = selectedTypes.indexOf(value);
      if (index !== -1) {
        selectedTypes.splice(index, 1);
      }
    }

    setFormData({
      ...formData,
      tutoringType: selectedTypes,
    });
  };

  const validateForm = () => {
    const validationErrors = {};
    if (!formData.currentStatus.trim()) {
      validationErrors.currentStatus = t('tutorProfile.currentStatusRequired');
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = () => {
    // Clear previous errors
    setErrors({});
    dispatch(clearTutor());
    
    if (validateForm()) {
      const data = new FormData();
      data.set('currentStatus', JSON.stringify(formData.currentStatus));
      data.set('tutoringType', JSON.stringify(formData.tutoringType));
      const dateAvailJson = JSON.stringify(formData.dateAvailability);
      data.set('dateAvailability', dateAvailJson);
      
      dispatch(updateTutor(data));
    } else {
      setOpenSnackbar(true);
      setSubmitStatus('error');
    }
  };

  const isInactiveStatus =
    formData.currentStatus === t('tutorProfile.inActive') || formData.currentStatus === 'Inactive';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        py: 6,
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/tutor-profile')}
          sx={{ mb: 3, color: theme.palette.primary.main }}
        >
          {t('common.back')}
        </Button>

        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            p: 4,
            background: 'white',
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <CalendarToday sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
              {t('tutorProfileDashboard.calendarAvailability')}
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {errors.general && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {errors.general}
            </Alert>
          )}

          <Stack spacing={4}>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}
              >
                {t('tutorProfile.availability')}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                  <TextField
                    select
                    label={t('tutorProfile.currentStatus')}
                    name="currentStatus"
                    value={formData.currentStatus}
                    error={Boolean(errors.currentStatus)}
                    helperText={errors.currentStatus}
                    onChange={handleChange}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  >
                    {ACTIVE_STATUS.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}
              >
                {t('tutorProfile.tutoringType')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <MuiCard
                    sx={{
                      p: 2,
                      border: `2px solid ${
                        formData.tutoringType.includes('REMOTE')
                          ? theme.palette.primary.main
                          : alpha(theme.palette.primary.main, 0.2)
                      }`,
                      borderRadius: 2,
                      bgcolor: formData.tutoringType.includes('REMOTE')
                        ? alpha(theme.palette.primary.main, 0.1)
                        : 'transparent',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.tutoringType.includes('REMOTE')}
                          onChange={handleTutoringTypeChange}
                          value="REMOTE"
                          color="primary"
                        />
                      }
                      label={t('common.online')}
                    />
                  </MuiCard>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MuiCard
                    sx={{
                      p: 2,
                      border: `2px solid ${
                        formData.tutoringType.includes('IN_PERSON')
                          ? theme.palette.primary.main
                          : alpha(theme.palette.primary.main, 0.2)
                      }`,
                      borderRadius: 2,
                      bgcolor: formData.tutoringType.includes('IN_PERSON')
                        ? alpha(theme.palette.primary.main, 0.1)
                        : 'transparent',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.tutoringType.includes('IN_PERSON')}
                          onChange={handleTutoringTypeChange}
                          value="IN_PERSON"
                          color="primary"
                        />
                      }
                      label={t('common.inPerson')}
                    />
                  </MuiCard>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MuiCard
                    sx={{
                      p: 2,
                      border: `2px solid ${
                        formData.tutoringType.includes('REMOTE') &&
                        formData.tutoringType.includes('IN_PERSON')
                          ? theme.palette.primary.main
                          : alpha(theme.palette.primary.main, 0.2)
                      }`,
                      borderRadius: 2,
                      bgcolor:
                        formData.tutoringType.includes('REMOTE') &&
                        formData.tutoringType.includes('IN_PERSON')
                          ? alpha(theme.palette.primary.main, 0.1)
                          : 'transparent',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            formData.tutoringType.includes('REMOTE') &&
                            formData.tutoringType.includes('IN_PERSON')
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                tutoringType: ['REMOTE', 'IN_PERSON'],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                tutoringType: [],
                              });
                            }
                          }}
                          color="primary"
                        />
                      }
                      label={t('tutorProfile.both')}
                    />
                  </MuiCard>
                </Grid>
              </Grid>
            </Box>

            {!isInactiveStatus && (
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.main }}
                >
                  {t('tutorProfile.calendar')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                  {t('tutorProfile.calendarInstructions')}
                </Typography>
                <DateAvailabilityCalendar
                  monthsToShow={4}
                  value={formData.dateAvailability}
                  onChange={(dateAvailability) => {
                    setFormData({
                      ...formData,
                      dateAvailability,
                    });
                  }}
                />
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/tutor-profile')}
                sx={{ borderRadius: 2 }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={tutorState.loading}
                sx={{
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  minWidth: '150px',
                }}
              >
                {tutorState.loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('common.save')
                )}
              </Button>
            </Box>
          </Stack>
        </Card>
      </Container>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={submitStatus === 'success' ? 'success' : 'error'}
          sx={{ borderRadius: 2 }}
        >
          {submitStatus === 'success'
            ? t('tutorProfile.profileUpdatedSuccess')
            : t('tutorProfile.profileUpdateFailed')}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TutorCalendarForm;

